import { useRef, useState, useMemo } from "react";
import Link from "next/link";
import Head from "next/head";
import {
  Printer,
  PageContainer,
  Doc,
  SecondaryContainer as CommonContainer,
  NoContent,
} from "components";
import { generateImage } from "utils/newFunction";
import clsx from "clsx";
import { useLocale } from "hooks";
import DateUtils from "utils/DateUtils";
import translation from "./translation";
import Toast from "components/Toast/Toast";
import NewsSlider from "components/NewsSlider";
import {
  replaceIfNotExist,
  wrapTablesInDiv,
  normalizeEditor,
} from "utils/newFunction";
import StyledNewsEventCard from "./NewsEventCard";
import Sharing from "./Sharing";
import Lead from "./Lead";
import Kicker from "./Kicker";
import { useRouter } from "next/router";

// type => news | conferences"
function NewsDetails({ className, data, suggestedItemsLabel, type }) {
  const news = data?.news;
  const [shareLink, setShareLink] = useState("");
  const componentRef = useRef(null);
  const { isRTL, locale, isFA } = useLocale();
  const htmlContent = useMemo(
    () => normalizeEditor(wrapTablesInDiv(news?.content?.value)),
    [news?.content?.value || ""],
  );
  const router = useRouter();
  return (
    <PageContainer
      className={className}
      breadCrumb={data?.breadcrumb || []}
      metaTag={replaceIfNotExist(
        news?.metaTag,
        replaceIfNotExist(news?.seo, {
          title: news?.["title"]?.value || "جزییات خبر",
        }),
      )}
      changeLanguageLink={data?.changeLanguageLink}
      canonical={data?.canonical}
      noIndex={Boolean(news?.noIndex?.value)}
    >
      <Toast />
      <CommonContainer
        ref={componentRef}
        withPrintTitle
        className="tw-rounded-2xl tw-bg-white tw-pb-4"
      >
        {news ? (
          <div className="tw-px-4 md:tw-px-0">
            <div className="tw-flex md:tw-block tw-flex-col tw-p-4">
              <div
                className={clsx(
                  "tw-p-5 tw-rounded-xl tw-border tw-border-solid tw-border-[#344456]/20 tw-w-full md:tw-w-1/2 tw-mb-[30px] md:tw-mb-0 tw-order-2",
                  {
                    "md:tw-float-right md:tw-ml-[30px]": isRTL,
                    "md:tw-float-left md:tw-mr-[30px]": !isRTL,
                  },
                )}
              >
                <NewsSlider
                  slides={news?.detailedimage?.value.map(generateImage)}
                  alt={news?.imageTag?.value || news?.title?.value}
                />
                <Sharing
                  shareLink={shareLink}
                  setShareLink={setShareLink}
                  videosButton={data?.videosButton}
                  shouldHideVideosButtonLink={data?.shouldHideVideosButtonLink}
                />
              </div>
              <div>
                {news?.lead?.value && <Lead>{news?.lead?.value}</Lead>}
                <h1 className="tw-text-[#344456] tw-font-bold tw-text-xl xxxl:tw-text-[26.59px] tw-leading-[45.09px]">
                  {news?.title?.value}
                </h1>
                <div className="tw-flex tw-justify-between tw-mt-3">
                  <p className="tw-text-[#344456]/40 tw-text-[13px] xxxl:tw-text-[17.29px]">
                    <span>{translation[locale]?.PUBLISH_DATE}: </span>
                    <span>
                      {isFA
                        ? DateUtils.parsePersianhNewsDate(
                            news?.publish_date?.value,
                          )
                        : DateUtils.parseEnglishNewsDate(
                            news?.publish_date?.value,
                          )}
                    </span>
                  </p>
                  <Printer
                    componentRef={componentRef}
                    trigger={
                      <button
                        type="button"
                        className="tw-border-0 tw-bg-zero tw-cursor-pointer print:tw-hidden"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.5 7.5V3.01758C17.5 2.68594 17.3684 2.36836 17.134 2.13359L15.366 0.366016C15.1316 0.131641 14.8137 0 14.482 0H3.75C3.05977 0 2.5 0.559766 2.5 1.25V7.5C1.11914 7.5 0 8.61914 0 10V14.375C0 14.7203 0.279687 15 0.625 15H2.5V18.75C2.5 19.4402 3.05977 20 3.75 20H16.25C16.9402 20 17.5 19.4402 17.5 18.75V15H19.375C19.7203 15 20 14.7203 20 14.375V10C20 8.61914 18.8809 7.5 17.5 7.5ZM15 17.5H5V13.75H15V17.5ZM15 8.75H5V2.5H12.5V4.375C12.5 4.72031 12.7797 5 13.125 5H15V8.75ZM16.875 11.5625C16.3574 11.5625 15.9375 11.1426 15.9375 10.625C15.9375 10.107 16.3574 9.6875 16.875 9.6875C17.3926 9.6875 17.8125 10.107 17.8125 10.625C17.8125 11.1426 17.3926 11.5625 16.875 11.5625Z"
                            fill="#344456"
                          />
                        </svg>
                      </button>
                    }
                  />
                </div>
                {news?.kicker?.value && <Kicker kicker={news?.kicker?.value} />}
                <Doc ltr={!isRTL}>
                  <div className="doc tw-mt-2">
                    {/* {HtmlParser(news?.content.value || "")} */}
                    <div
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    ></div>
                  </div>
                </Doc>
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-mb-4">
            <NoContent />
          </div>
        )}
        <div className="tw-clear-both"></div>
      </CommonContainer>
      <CommonContainer className="tw-px-4 md:tw-px-0">
        <div
          className={clsx({
            "tw-pt-6 tw-border-0 tw-border-t tw-border-solid tw-border-[#E5E9F4] tw-mt-0 md:tw-mt-6":
              news?.tags?.length > 0,
          })}
        >
          {news?.tags?.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-4">
              <span className="tw-self-center">
                {translation[locale]?.TAGS}:
              </span>
              {news?.tags?.map?.((title) => (
                <div
                  className={clsx(
                    "tw-bg-[#EFF2F9] tw-border-[#EFF2F9] tw-rounded-[34px] tw-text-[#344456] tw-py-1.5 tw-px-5 tw-font-medium tw-text-sm",
                  )}
                  key={title}
                >
                  {title}
                </div>
              ))}
            </div>
          )}
        </div>
      </CommonContainer>
      <section
        className={clsx("tw-bg-[#EFF2F9] ", {
          "tw-mt-[70px]": news?.tags?.length > 0,
          "tw-mt-10": news?.tags?.length === 0,
        })}
      >
        <div className="tw-px-4 md:tw-px-0">
          <CommonContainer>
            <div className="tw-pt-[30px] xxxl:tw-pt-10 tw-pb-[50px] xxxl:tw-pb-[66.5px]">
              <div className="tw-flex tw-justify-between tw-h-fit tw-w-full">
                <h5
                  className={clsx(
                    "tw-text-[#344456] tw-font-bold tw-text-base xxxl:tw-text-[21.27px] tw-border-solid tw-border-y-0 tw-border-[#007FAC]",
                    {
                      "tw-border-r-4 tw-border-l-0 tw-pr-2.5": isRTL,
                      "tw-border-l-4 tw-border-r-0 tw-pl-2.5": !isRTL,
                    },
                  )}
                >
                  {suggestedItemsLabel?.[locale]}
                </h5>
                <Link href={`/${type}`} passHref prefetch={false}>
                  <a className="tw-flex tw-items-center md:tw-text-sm xxxl:tw-text-[18.61px] tw-font-medium tw-text-[#007FAC] hover:tw-text-[#007FAC]/70 tw-transition-all tw-group">
                    <span
                      className={clsx({ "tw-ml-3": isRTL, "tw-mr-3": !isRTL })}
                    >
                      {translation[locale]?.SEE_MORE}
                    </span>
                    <svg
                      width="8"
                      height="13"
                      viewBox="0 0 8 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={clsx(
                        "tw-relative tw-transition-all tw-fill-[#007FAC] group-hover:tw-fill-[#007FAC]/70",
                        {
                          "tw-right-0 group-hover:tw-right-1": isRTL,
                          "tw-left-0 group-hover:tw-left-1": !isRTL,
                        },
                      )}
                      style={!isRTL ? { transform: "rotate(180deg)" } : {}}
                    >
                      <path
                        d="M7.41992 1.58981L2.82992 6.17981L7.41992 10.7698L5.99992 12.1798L-7.84636e-05 6.17981L5.99992 0.17981L7.41992 1.58981Z"
                        fill=""
                      />
                    </svg>
                  </a>
                </Link>
              </div>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-x-[30px] lg:tw-gap-x-[75px] xxxl:tw-gap-x-[99.73px] tw-gap-y-[30px] tw-mt-[30px]">
                {data?.latestNews?.map((v, i) => (
                  <StyledNewsEventCard v={v} key={i} type={type} />
                ))}
              </div>
            </div>
          </CommonContainer>
        </div>
      </section>
      <Head>
        <script type="application/ld+json">
          {`{
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "khatam.ac.ir${router.asPath?.replaceAll?.('"', "") || ""}"
            },
            "headline": "${news?.title?.value?.replaceAll?.('"', "") || ""}",
            "description": "${
              news?.metaTag?.description?.replaceAll?.('"', "") ||
              news?.seo?.description?.replaceAll?.('"', "") ||
              ""
            }",
            "image": "http://podspace.pod.ir/api/images/${
              news?.Thumbnail?.value?.replaceAll?.('"', "") || ""
            }",
            "author": {
              "@type": "Organization",
              "name": "دانشگاه خاتم",
              "url": "https://khatam.ac.ir/"
            },
            "publisher": {
              "@type": "Organization",
              "name": "khatam university",
              "logo": {
                "@type": "ImageObject",
                "url": "https://podspace.pod.ir/api/images/${
                  locale === "fa" ? "RP3RSKQEXQFA4V4P" : "ZHDUU8WNMDCJBBJS"
                }"
              }
            },
            "datePublished": "${DateUtils.parseSchemaNewsDate(
              news?.publish_date?.value?.replaceAll?.('"', ""),
            )}"
          }`}
        </script>
      </Head>
    </PageContainer>
  );
}

export default NewsDetails;
